import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bot, Plus, Trash2 } from 'lucide-react';

import { listAgents } from '../../api/agents';
import {
  addProjectAgent,
  getProjectRepository,
  listProjectAgents,
  removeProjectAgent,
} from '../../api/integrations';
import { queryKeys } from '../../api/queryKeys';
import { Badge } from '../../components/Badge';
import { Button } from '../../components/Button';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Skeleton } from '../../components/Skeleton';
import { useToast } from '../../components/Toast';
import { humanizeError } from '../../lib/errors';
import styles from './SettingsPages.module.css';

export interface ProjectAgentsSectionProps {
  readonly projectKey: string;
}

type SharedRole = 'analyst' | 'developer';

const ROLE_TEXT: Record<SharedRole, { title: string; hint: string; added: string }> = {
  analyst: {
    title: 'AI-аналитик',
    hint: 'Нужны настроенный репозиторий проекта и статусы workflow. Добавление не требует SSH и переустановки агента.',
    added: 'AI-аналитик добавлен в проект',
  },
  developer: {
    title: 'AI-разработчик',
    hint: 'Нужны настроенный репозиторий, write+PR токен и хотя бы одна команда проверок.',
    added: 'AI-разработчик добавлен в проект',
  },
};

/**
 * Общие AI-агенты в проекте (docs/17 §7, §12.2): добавить/удалить, увидеть
 * статусы. Репозиторий проекта настраивается блоком выше.
 */
export function ProjectAgentsSection({ projectKey }: ProjectAgentsSectionProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [removeRole, setRemoveRole] = useState<SharedRole | null>(null);

  const agentsQuery = useQuery({
    queryKey: queryKeys.agents.list,
    queryFn: ({ signal }) => listAgents(signal),
    staleTime: 15_000,
  });
  const bindingsQuery = useQuery({
    queryKey: ['projects', projectKey, 'agents'],
    queryFn: ({ signal }) => listProjectAgents(projectKey, signal),
    enabled: projectKey.length > 0,
  });
  const repositoryQuery = useQuery({
    queryKey: ['projects', projectKey, 'repository'],
    queryFn: ({ signal }) => getProjectRepository(projectKey, signal),
    enabled: projectKey.length > 0,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['projects', projectKey, 'agents'] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.agents.list });
  };

  const reportError = (cause: unknown): void => {
    const human = humanizeError(cause);
    toast.error(human.message, { code: human.code });
  };

  const addMutation = useMutation({
    mutationFn: (agentId: string) => addProjectAgent(projectKey, agentId),
    onSuccess: () => {
      invalidate();
      toast.success('Общий AI-агент добавлен в проект');
    },
    onError: reportError,
  });

  const removeMutation = useMutation({
    mutationFn: (agentId: string) => removeProjectAgent(projectKey, agentId),
    onSuccess: () => {
      invalidate();
      setRemoveRole(null);
      toast.success('Общий AI-агент удалён из проекта');
    },
    onError: reportError,
  });

  if (agentsQuery.isPending || bindingsQuery.isPending) {
    return <Skeleton variant="row" count={3} />;
  }

  const agents = agentsQuery.data?.items ?? [];
  const bindings = bindingsQuery.data?.items ?? [];
  const repoConfigured = (repositoryQuery.data?.url ?? null) !== null;
  const repoWriteReady =
    repoConfigured &&
    repositoryQuery.data?.hasWriteToken === true &&
    (repositoryQuery.data?.checks ?? []).length > 0;

  const card = (role: SharedRole) => {
    const agent = agents.find(
      (item) => item.isShared === true && item.agentRole === role,
    );
    const binding = bindings.find((item) => item.agentRole === role);
    const text = ROLE_TEXT[role];
    const ready = role === 'analyst' ? repoConfigured : repoWriteReady;

    return (
      <section className={styles['section']} key={role}>
        <div className={styles['sectionHeader']}>
          <h2>{text.title}</h2>
          {binding ? <Badge tone="success">Добавлен</Badge> : null}
        </div>

        {agent === undefined ? (
          <p className={styles['hint']}>
            {role === 'analyst' ? 'AI-аналитик' : 'AI-разработчик'} ещё не
            создан. Его создаёт Admin на странице «AI-агенты».
          </p>
        ) : binding ? (
          <>
            <p>
              <Bot aria-hidden size={16} /> {agent.displayName} · @{agent.login}
            </p>
            <p className={styles['hint']}>
              Статусы: {binding.statuses.trigger} → {binding.statuses.work} →{' '}
              {binding.statuses.review} → {binding.statuses.approved}
            </p>
            <div className={styles['actions']}>
              <Button
                variant="danger"
                size="sm"
                icon={<Trash2 size={14} />}
                onClick={() => setRemoveRole(role)}
              >
                Удалить из проекта
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className={styles['hint']}>{text.hint}</p>
            {ready ? null : (
              <p className={styles['hint']}>
                {role === 'analyst'
                  ? 'Сначала настройте репозиторий проекта.'
                  : 'Сначала настройте репозиторий, write+PR токен и команды проверок.'}
              </p>
            )}
            <div className={styles['actions']}>
              <Button
                variant="primary"
                size="sm"
                icon={<Plus size={14} />}
                disabled={!ready}
                loading={addMutation.isPending}
                onClick={() => addMutation.mutate(agent.id)}
              >
                Добавить в проект
              </Button>
            </div>
          </>
        )}
      </section>
    );
  };

  return (
    <>
      {card('analyst')}
      {card('developer')}
      {removeRole !== null ? (
        <ConfirmDialog
          open
          title={`Удалить AI-${removeRole === 'analyst' ? 'аналитика' : 'разработчика'} из проекта?`}
          description={
            removeRole === 'analyst'
              ? 'Проектный токен будет отозван, агент перестанет опрашивать проект. Пока у агента есть открытые задачи в проекте, отвязка запрещена — сначала переназначьте их.'
              : 'Проектный токен будет отозван, агент перестанет опрашивать проект. Открытые PR останутся на ревью.'
          }
          confirmLabel="Удалить"
          isPending={removeMutation.isPending}
          onConfirm={() => {
            const binding = bindings.find((item) => item.agentRole === removeRole);
            if (binding) removeMutation.mutate(binding.agentId);
          }}
          onClose={() => setRemoveRole(null)}
        />
      ) : null}
    </>
  );
}
